import { Fragment, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { capitalize, debounce } from 'lodash';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { apolloCache } from 'src/graphql';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { Table, TableBody, TableCell, TableRow, TableHeader, TableHead } from '@/components/ui/table';
import {
  EscrowPartyTypeEnum,
  type IBeneficiary,
  type IDepositor,
  type IContact,
  AgreementFormEnum,
  EscrowStatusEnum,
} from 'src/graphql/schema';
import {
  SuggestedChangesDocument,
  ISuggestedChangesQuery,
} from 'src/graphql/actions/suggestChanges/suggestedChanges.query';
import { InputSearch } from '@/components/ui/input-search';
import env from 'env';
import { SuggestedChangesView } from 'src/components/SuggestChanges';
import {
  getEditedContactsSuggestedChangesItems,
  getSignatorySuggestedChangesItems,
  getNewContactsSuggestedChangesItems,
  getDeletedContactsSuggestedChangesItems,
} from 'src/containers/SoftwareEscrow/RequestChanges/Party/lib';
import Paginator from '@/components/ui/paginator';

const PER_PAGE = 10;

const agreementFormText = {
  base: 'The individuals acting as points of contact for this organization, and the representative(s) who will be responsible for signing the agreement, as listed in the escrow agreement.',
  setup: (
    <Fragment>
      The individuals acting as points of contact for this organization, and the representative(s) who will be
      responsible for signing the agreement, as listed in the escrow agreement.
      <br />
      <br />
      Note: Once all parties approve the escrow, we’ll send e-signature requests via DropBox Sign to the designated
      signatories to make the agreement legally binding.
      <br />
      Need a different signing method or a hardcopy agreement?{' '}
      <a href={`mailto:${env.CODEKEEPER_SERVICE_EMAIL}`} className="underline! text-muted-foreground!">
        Contact us
      </a>
      .
    </Fragment>
  ),
  beneficiaryBipartite:
    'The individuals acting as points of contact for this organization, as listed in the escrow agreement.',
};

const RepresentativesSuggestedChanges = ({ partyType }: { partyType: EscrowPartyTypeEnum }) => {
  const { softwareEscrow } = useSoftwareEscrow();
  const data = apolloCache.readQuery<ISuggestedChangesQuery>({
    query: SuggestedChangesDocument,
    variables: {
      escrowId: softwareEscrow.id,
      changeType: `${capitalize(partyType)}SuggestedChange`,
    },
  });

  if (!data?.suggestedChanges.nodes.length) return null;

  const { nodes } = data.suggestedChanges;
  const { payload } = nodes[0];

  if (!payload) return null;

  const partyContacts = softwareEscrow[partyType]?.contacts || [];

  const partyContactsDictionary = partyContacts.reduce<Record<string, IContact>>((acc, contact) => {
    acc[contact.id] = contact;
    return acc;
  }, {});

  const getSuggestedChangesItems = () => {
    const items = [];

    if (payload.edited_contacts)
      items.push(
        ...getEditedContactsSuggestedChangesItems(payload.edited_contacts, partyContacts),
        ...getSignatorySuggestedChangesItems(payload.edited_contacts, partyContacts),
      );

    if (payload.new_contacts) items.push(...getNewContactsSuggestedChangesItems(payload.new_contacts));

    if (payload.deleted_contacts) {
      const deletedContacts = payload.deleted_contacts
        .map((deletedContact: { id: string }) => partyContactsDictionary[deletedContact.id])
        .filter(Boolean);

      if (deletedContacts.length) {
        items.push(...getDeletedContactsSuggestedChangesItems(deletedContacts));
      }
    }

    return items;
  };

  const suggestedChangesItems = getSuggestedChangesItems();

  return <SuggestedChangesView items={suggestedChangesItems} />;
};

const columnHelper = createColumnHelper<IContact>();

const baseColumns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (props) => <div className="text-sm">{props.getValue()}</div>,
    meta: {
      headerClassName: 'max-w-[164px]',
      cellClassName: 'max-w-[164px]',
    },
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: (props) => (
      <div className="text-sm truncate" title={props.getValue()}>
        {props.getValue()}
      </div>
    ),
    meta: {
      headerClassName: 'max-w-[164px]',
      cellClassName: 'max-w-[164px]',
    },
  }),
  columnHelper.accessor('phone', {
    header: 'Phone number',
    cell: (props) => <div className="text-sm">{props.getValue() || '-'}</div>,
  }),
  columnHelper.accessor('signatory', {
    header: 'Signatory',
    cell: (props) =>
      props.getValue() ? (
        <div className="size-3.5 rounded-full flex items-center justify-center bg-secondary-foreground mx-auto">
          <Check className="size-2.5 text-white" />
        </div>
      ) : null,
    meta: {
      headerClassName: 'w-[96px]',
      cellClassName: 'w-[96px]',
    },
  }),
];

const Representatives = ({ partyType }: { partyType: EscrowPartyTypeEnum }) => {
  const {
    softwareEscrow,
    softwareEscrow: { agreement, status },
  } = useSoftwareEscrow();

  const party = softwareEscrow[partyType] as IDepositor | IBeneficiary;

  const [representatives, setRepresentatives] = useState<IContact[]>(party?.contacts || []);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PER_PAGE,
  });

  const debouncedOnSearch = debounce((e) => {
    const searchValue = e.target.value;

    if (!searchValue) return setRepresentatives(party?.contacts || []);

    const filtered = party?.contacts?.filter(({ name }) => name.toLowerCase().includes(searchValue.toLowerCase()));

    setRepresentatives(filtered || []);
  }, 500);

  const onClear = () => setRepresentatives(party?.contacts || []);

  const isBeneficiaryBipartite =
    partyType === EscrowPartyTypeEnum.Beneficiary && agreement?.agreementForm === AgreementFormEnum.Bipartite;

  const columns = useMemo(() => {
    const columns = [...baseColumns];

    if (isBeneficiaryBipartite) columns.pop();

    return columns;
  }, [isBeneficiaryBipartite]);

  const table = useReactTable({
    data: representatives,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const onPageChange = (pageNumber: number) =>
    setPagination({
      pageIndex: pageNumber - 1,
      pageSize: PER_PAGE,
    });

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-bold">{capitalize(partyType)} representatives & signatories</h2>
        <p className="text-muted-foreground text-sm">
          {getAgreementText(
            isBeneficiaryBipartite,
            status === EscrowStatusEnum.AdminReview || status === EscrowStatusEnum.Onboarding,
          )}
        </p>
      </div>
      <RepresentativesSuggestedChanges partyType={partyType} />
      <div className="space-y-4">
        <div className="w-[373px] max-w-full">
          <InputSearch placeholder="Search" onChange={debouncedOnSearch} isLoading={false} onClear={onClear} />
        </div>
        <div className="space-y-2">
          <div className="border border-border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  {table.getFlatHeaders().map((header) => (
                    <TableHead key={header.id} className={header.column.columnDef.meta?.headerClassName}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className={cell.column.columnDef.meta?.cellClassName}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={table.getFlatHeaders().length} className="text-center">
                      No representatives found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <Paginator
            currentPage={pagination.pageIndex + 1}
            totalPages={Math.ceil(representatives.length / PER_PAGE)}
            onPageChange={onPageChange}
            showPreviousNext
            hideOnSinglePage
          />
        </div>
      </div>
    </div>
  );
};

function getAgreementText(isBeneficiaryBipartite: boolean, isSetup: boolean) {
  if (isSetup) return agreementFormText.setup;

  if (isBeneficiaryBipartite) return agreementFormText.beneficiaryBipartite;

  return agreementFormText.base;
}

export default Representatives;
